import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import axios from 'axios';
import CloudIcon from '@mui/icons-material/Cloud';

// Componente para exibir a temperatura na barra inferior
const WeatherWidget = () => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({
    unidade: 'C' // 'C' para Celsius, 'F' para Fahrenheit
  });
  
  // Carregar configurações
  useEffect(() => {
    const loadConfig = () => {
      const savedConfig = localStorage.getItem('painelChamada_configCompleta');
      if (savedConfig) {
        try {
          const parsedConfig = JSON.parse(savedConfig);
          if (parsedConfig.clima) {
            setConfig(parsedConfig.clima);
          }
        } catch (error) {
          console.error('Erro ao carregar configurações do clima:', error);
        }
      }
    };
    
    loadConfig();
    
    // Monitorar alterações nas configurações
    const configObserver = (event) => {
      if (event.key === 'painelChamada_configCompleta') {
        loadConfig();
      }
    };
    
    window.addEventListener('storage', configObserver);
    
    return () => {
      window.removeEventListener('storage', configObserver);
    };
  }, []);
  
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        // Usar API Open-Meteo que não requer chave API
        const response = await axios.get(
          `https://api.open-meteo.com/v1/forecast?latitude=-23.55&longitude=-46.64&current=temperature_2m,weather_code&timezone=America/Sao_Paulo`,
          { timeout: 5000 } 
        );
        
        // Mapear código do clima para descrição
        const weatherCode = response.data.current.weather_code;
        let description = 'NUBLADO';
        
        // Mapear códigos de clima para descrições em português
        // https://open-meteo.com/en/docs/weather-code-mappings#:~:text=WMO%20Weather%20interpretation%20codes%20(WW)%3A,-Code&text=0%3A%20Clear%20sky,3%3A%20Overcast
        if (weatherCode === 0) description = 'CÉU LIMPO';
        else if (weatherCode === 1) description = 'PARCIALMENTE NUBLADO';
        else if (weatherCode === 2) description = 'NUBLADO';
        else if (weatherCode === 3) description = 'ENCOBERTO';
        else if ([45, 48].includes(weatherCode)) description = 'NEBLINA';
        else if ([51, 53, 55, 56, 57].includes(weatherCode)) description = 'CHUVISCO';
        else if ([61, 63, 65, 66, 67].includes(weatherCode)) description = 'CHUVA';
        else if ([71, 73, 75, 77].includes(weatherCode)) description = 'NEVE';
        else if ([80, 81, 82].includes(weatherCode)) description = 'PANCADAS DE CHUVA';
        else if ([95, 96, 99].includes(weatherCode)) description = 'TEMPESTADE';
        
        // Formatar dados no mesmo formato esperado pelo componente
        setWeather({
          main: { temp: response.data.current.temperature_2m },
          weather: [{ description }],
        });
      } catch (error) {
        console.warn('Usando dados mockados para o clima:', error.message || error);
        // Usar dados mockados em caso de falha
        setWeather({
          main: { temp: 25 },
          weather: [{ description: 'NUBLADO' }],
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchWeather();
    const interval = setInterval(fetchWeather, 30 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Converter Celsius para Fahrenheit
  const celsiusParaFahrenheit = (celsius) => {
    return (celsius * 9/5) + 32;
  };
  
  // Formatar temperatura de acordo com a configuração
  const getFormattedTemperature = () => {
    if (!weather || !weather.main) return '--°C';
    
    if (config.unidade === 'C') {
      return `${Math.round(weather.main.temp)}°C`;
    } else {
      const tempFahrenheit = celsiusParaFahrenheit(weather.main.temp);
      return `${Math.round(tempFahrenheit)}°F`;
    }
  };
  
  if (loading || !weather || !weather.main || !weather.weather) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CloudIcon sx={{ mr: 2, fontSize: 28 }} />
          <Typography variant="h3" sx={{ fontWeight: 'light' }}>
            --°{config.unidade}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ mt: 1, fontSize: '0.8rem' }}>
          {loading ? "CARREGANDO" : "INDISPONÍVEL"}
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: 'white' }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <CloudIcon sx={{ mr: 2, fontSize: 28 }} />
        <Typography variant="h3" sx={{ fontWeight: 'light' }}>
          {getFormattedTemperature()}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ mt: 1, fontSize: '0.8rem', textTransform: 'uppercase' }}>
        {weather.weather[0]?.description || "NUBLADO"}
      </Typography>
    </Box>
  );
};

export default WeatherWidget; 